import React, { useCallback, useState } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

export interface UploadedPhoto {
  id: string;
  file: File;
  previewUrl: string;
  name: string;
}

interface Props {
  photos: UploadedPhoto[];
  onPhotosChange: (photos: UploadedPhoto[]) => void;
  maxFiles?: number;
}

let idCounter = 0;
const makeId = () => `photo_${++idCounter}_${Date.now()}`;

export const BatchUploadZone: React.FC<Props> = ({ photos, onPhotosChange, maxFiles = 50 }) => {
  const [dragOver, setDragOver] = useState(false);

  const addFiles = useCallback((files: FileList | File[]) => {
    const newPhotos: UploadedPhoto[] = [];
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) continue;
      if (photos.length + newPhotos.length >= maxFiles) break;
      newPhotos.push({
        id: makeId(),
        file,
        previewUrl: URL.createObjectURL(file),
        name: file.name,
      });
    }
    if (newPhotos.length > 0) {
      onPhotosChange([...photos, ...newPhotos]);
    }
  }, [photos, onPhotosChange, maxFiles]);

  const removePhoto = (id: string) => {
    const photo = photos.find(p => p.id === id);
    if (photo) URL.revokeObjectURL(photo.previewUrl);
    onPhotosChange(photos.filter(p => p.id !== id));
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  return (
    <div>
      {/* Drop zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
          dragOver ? 'border-accent bg-accent/5' : 'border-border hover:border-border-hover'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById('batchFileInput')?.click()}
      >
        <input
          type="file"
          id="batchFileInput"
          className="hidden"
          accept="image/*"
          multiple
          onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
        />
        <Upload size={24} className="mx-auto mb-3 text-muted" />
        <p className="text-sm font-medium mb-1">
          {photos.length === 0 ? 'Sleep je foto\'s hierheen' : 'Voeg meer foto\'s toe'}
        </p>
        <p className="text-xs text-muted">
          Of klik om bestanden te selecteren. Maximaal {maxFiles} foto's.
        </p>
      </div>

      {/* Thumbnail grid */}
      {photos.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-muted">{photos.length} foto{photos.length !== 1 ? '\'s' : ''} geüpload</p>
            {photos.length > 1 && (
              <button
                onClick={() => { photos.forEach(p => URL.revokeObjectURL(p.previewUrl)); onPhotosChange([]); }}
                className="text-xs text-muted hover:text-red-500 transition-colors"
              >
                Alles verwijderen
              </button>
            )}
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
            {photos.map(photo => (
              <div key={photo.id} className="relative group aspect-square rounded-md overflow-hidden border border-border">
                <img src={photo.previewUrl} alt={photo.name} className="w-full h-full object-cover" />
                <button
                  onClick={(e) => { e.stopPropagation(); removePhoto(photo.id); }}
                  className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={10} />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5">
                  <p className="text-[8px] text-white truncate">{photo.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
